if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.carouselTrack = null;
        this.carouselDots = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        this.carouselTrack = null;
        this.carouselDots = null;

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading__spinner').classList.remove('hidden');

      const productUrl = opener.getAttribute('data-product-url');
      this.quickAddMode = this.dataset.quickAddMode || 'single';
      this.paginationStyle = this.dataset.quickAddPagination || 'dots';
      this.modalTitle = this.dataset.quickAddTitle || 'Quick add';
      this.showQuantity = this.dataset.quickAddShowQuantity === 'true';

      this.ensureCarousel();

      if (this.quickAddMode === 'single') {
        this.carouselTrack.innerHTML = '';
        this.carouselTrack.scrollLeft = 0;
        this.carouselDots.innerHTML = '';
        this.addSlide(productUrl, opener, true);
      } else {
        this.carouselTrack.innerHTML = '';
        this.carouselTrack.scrollLeft = 0;
        this.carouselDots.innerHTML = '';
        const list = opener.closest('.cart-notification-recommendations__list');
        const buttons = list
          ? Array.from(list.querySelectorAll('.quick-add__submit'))
          : [opener];
          buttons.forEach((btn) => {
            const url = btn.getAttribute('data-product-url');
            this.addSlide(url, opener, url === productUrl);
          });
        }

        super.show(opener);
        opener.removeAttribute('aria-disabled');
        opener.classList.remove('loading');
        opener.querySelector('.loading__spinner').classList.add('hidden');
      }

      addSlide(productUrl, opener, makeActive = false) {
        if (!productUrl) return;
        const existing = this.carouselTrack.querySelector(
          `.quick-add-carousel__slide[data-product-url="${productUrl}"]`
        );
        const slide = existing || document.createElement('div');
        slide.className = 'quick-add-carousel__slide is-loading';
        slide.dataset.productUrl = productUrl;
        if (!existing) this.carouselTrack.appendChild(slide);

        if (makeActive) this.setActiveSlide(slide);
        this.updateDots();

        fetch(productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) return;

            this.preprocessHTML(productElement);
            slide.innerHTML = '';
            slide.appendChild(productElement);
            slide.classList.remove('is-loading');
            this.setupInfoScrollIndicator(slide);
            this.updateDots();

            if (makeActive) this.setActiveSlide(slide);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();
          })
          .catch((e) => {
            console.error(e);
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        productElement.dataset.skipVariantMediaPrune = 'true';
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.applyMinimalLayout(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      applyMinimalLayout(productElement) {
        const minimal = this.dataset.quickAddMinimal === 'true';
        const showMedia = this.dataset.quickAddShowMedia === 'true';
        const compactMedia = this.dataset.quickAddCompactMedia === 'true';
        const compactMediaDesktop = this.dataset.quickAddCompactMediaDesktop === 'true';
        const isDesktop = window.matchMedia('(min-width: 750px)').matches;
        const shouldCompactMedia = isDesktop ? compactMediaDesktop : compactMedia;

        productElement.classList.toggle('quick-add--compact-media', shouldCompactMedia);
        productElement.classList.toggle('quick-add--compact-media-desktop', compactMediaDesktop && isDesktop);
        productElement.classList.toggle('quick-add--compact-media-desktop-off', isDesktop && !compactMediaDesktop);

        if (!minimal) return;

        productElement.classList.add('quick-add--minimal');

        const mediaWrapper = productElement.querySelector('.product__media-wrapper');
        if (mediaWrapper) {
          if (!showMedia) {
            mediaWrapper.remove();
          } else if (shouldCompactMedia) {
            const gallery = mediaWrapper.querySelector('media-gallery');
            if (gallery) {
              const images = Array.from(gallery.querySelectorAll('li[data-media-id] img'));
              if (images.length) {
                const simpleGallery = document.createElement('div');
                simpleGallery.classList.add('quick-add-minimal-media');
                images.forEach((img) => {
                  const clone = img.cloneNode(true);
                  clone.removeAttribute('loading');
                  simpleGallery.appendChild(clone);
                });
                mediaWrapper.innerHTML = '';
                mediaWrapper.appendChild(simpleGallery);
              }
            }
          }
        }

        if (!this.showQuantity) {
          productElement.querySelectorAll('.product-form__quantity, quantity-input').forEach((el) => el.remove());
        }
      }

      ensureCarousel() {
        if (this.carouselTrack) {
          this.carouselTrack.parentNode.classList.toggle(
            'quick-add-carousel--single',
            this.quickAddMode === 'single'
          );
          return;
        }
        const wrapper = document.createElement('div');
        wrapper.className = 'quick-add-carousel';
        wrapper.classList.toggle('quick-add-carousel--single', this.quickAddMode === 'single');

        const track = document.createElement('div');
        track.className = 'quick-add-carousel__track';
        this.carouselTrack = track;

        const controls = document.createElement('div');
        controls.className = 'quick-add-carousel__controls';
        const prev = document.createElement('button');
        prev.type = 'button';
        prev.className = 'quick-add-carousel__btn quick-add-carousel__btn--prev';
        prev.innerHTML = '&#8249;';
        const next = document.createElement('button');
        next.type = 'button';
        next.className = 'quick-add-carousel__btn quick-add-carousel__btn--next';
        next.innerHTML = '&#8250;';
        prev.addEventListener('click', () => this.shiftSlide(-1));
        next.addEventListener('click', () => this.shiftSlide(1));
        controls.appendChild(prev);
        controls.appendChild(next);

        const dots = document.createElement('div');
        dots.className = 'quick-add-carousel__dots';
        this.carouselDots = dots;

        const title = document.createElement('div');
        title.className = 'quick-add-modal__title';
        title.textContent = this.modalTitle;

        wrapper.appendChild(title);
        wrapper.appendChild(track);
        wrapper.appendChild(controls);
        wrapper.appendChild(dots);

        this.modalContent.innerHTML = '';
        this.modalContent.appendChild(wrapper);

        if (!this.carouselTrackScrollHandler) {
          this.carouselTrackScrollHandler = this.handleTrackScroll.bind(this);
        }
        this.carouselTrack.addEventListener('scroll', this.carouselTrackScrollHandler, { passive: true });
      }

      setActiveSlide(targetSlide, skipScroll = false) {
        if (!this.carouselTrack) return;
        const slides = Array.from(this.carouselTrack.children);
        const targetIndex = slides.indexOf(targetSlide);
        slides.forEach((slide) => slide.classList.toggle('is-active', slide === targetSlide));
        if (targetIndex >= 0 && !skipScroll) {
          const offset = targetIndex * this.carouselTrack.clientWidth;
          this.carouselTrack.scrollTo({ left: offset, behavior: 'smooth' });
        }
        this.updateDots();
      }

      handleTrackScroll() {
        if (!this.carouselTrack) return;
        const width = this.carouselTrack.clientWidth;
        if (!width) return;
        const index = Math.round(this.carouselTrack.scrollLeft / width);
        const slides = Array.from(this.carouselTrack.children);
        const target = slides[index];
        if (target && !target.classList.contains('is-active')) {
          this.setActiveSlide(target, true);
        }
      }

      shiftSlide(delta) {
        if (!this.carouselTrack || !this.carouselTrack.children.length) return;
        const slides = Array.from(this.carouselTrack.children);
        const currentIndex = slides.findIndex((s) => s.classList.contains('is-active'));
        const nextIndex = (currentIndex + delta + slides.length) % slides.length;
        this.setActiveSlide(slides[nextIndex]);
      }

      updateDots() {
        if (!this.carouselTrack || !this.carouselDots) return;
        this.carouselDots.innerHTML = '';
        const slides = Array.from(this.carouselTrack.children);
        slides.forEach((slide, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'quick-add-carousel__dot';
          dot.setAttribute('aria-label', `Slide ${index + 1}`);
          dot.classList.toggle('is-active', slide.classList.contains('is-active'));
          if (this.paginationStyle === 'numbers') {
            dot.textContent = index + 1;
          }
          dot.addEventListener('click', () => this.setActiveSlide(slide));
          this.carouselDots.appendChild(dot);
        });
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      setupInfoScrollIndicator(root) {
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        const infoWrapper = root.querySelector('.product__info-wrapper.grid__item');
        if (!infoWrapper || infoWrapper.dataset.scrollIndicatorReady) return;

        infoWrapper.dataset.scrollIndicatorReady = 'true';

        const indicator = document.createElement('span');
        indicator.className = 'quick-add-scroll-indicator';
        infoWrapper.appendChild(indicator);

        const update = () => {
          const scrollHeight = infoWrapper.scrollHeight;
          const clientHeight = infoWrapper.clientHeight;
          const isScrollable = scrollHeight > clientHeight + 1;
          infoWrapper.dataset.scrollable = isScrollable ? 'true' : 'false';

          if (!isScrollable) {
            indicator.style.setProperty('--scroll-thumb-height', '0px');
            indicator.style.setProperty('--scroll-thumb-top', '0px');
            return;
          }

          const thumbHeight = Math.max(24, (clientHeight / scrollHeight) * clientHeight);
          const maxTop = Math.max(0, clientHeight - thumbHeight);
          const top = scrollHeight === clientHeight ? 0 : (infoWrapper.scrollTop / (scrollHeight - clientHeight)) * maxTop;

          indicator.style.setProperty('--scroll-thumb-height', `${thumbHeight}px`);
          indicator.style.setProperty('--scroll-thumb-top', `${top}px`);
        };

        infoWrapper.addEventListener('scroll', update, { passive: true });

        if ('ResizeObserver' in window) {
          const observer = new ResizeObserver(update);
          observer.observe(infoWrapper);
        } else {
          window.addEventListener('resize', update);
        }

        requestAnimationFrame(update);
      }
    }
  );
}
